import { ReactNode } from 'react';
import OverlayPanel from '@/components/ui/OverlayPanel';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ConfirmDialogProps {
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  children: ReactNode;
  /** Shown above the buttons — the server's refusal, once it has answered. */
  error?: string | null;
  isPending?: boolean;
}

/**
 * Every destructive action in the console goes through this. The consequence is spelled
 * out in the body rather than in the button, because "Delete" reads the same whether it
 * takes a row or a year of a clinic's work with it.
 */
export function ConfirmDialog({
  title,
  confirmLabel,
  onConfirm,
  onClose,
  children,
  error,
  isPending = false,
}: ConfirmDialogProps) {
  return (
    <OverlayPanel title={title} size="lg" onClose={isPending ? () => {} : onClose}>
      <div className="flex flex-col gap-4 text-white">
        <div className="space-y-2 text-sm text-white/75">{children}</div>

        {error && (
          <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {error}
          </p>
        )}

        <footer className="flex justify-end gap-2 border-t border-white/8 pt-3">
          <Button variant="outline-white" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending && <LoadingSpinner size={16} glowIntensity={null} />}
            {confirmLabel}
          </Button>
        </footer>
      </div>
    </OverlayPanel>
  );
}

export default ConfirmDialog;
